/**
 * Base Dexie (IndexedDB) de la couche hors-ligne terrain — Lots C & D.
 *
 * Socle du mode « hors-ligne first » (règle projet #5). Toute saisie terrain
 * (pesée au pressoir, dégorgement en cave) est d'abord écrite EN LOCAL ici,
 * puis poussée vers l'API tRPC dès que le réseau revient. Aucune frappe n'est
 * perdue si l'app se ferme : les files survivent au rechargement (IndexedDB).
 *
 * Files d'attente (`peseesQueue`, `degorgementsQueue`) : le payload stocké est
 * EXACTEMENT l'input du contrat partagé (`peseeCreerInput` /
 * `degorgementCreerInput`) — validation Zod symétrique avec le back. Chaque
 * entrée porte un `domaineId` indexé (isolation règle #3) : lectures, synchro
 * et écritures sont scopées au tenant courant, et les files sont purgées au
 * logout / switch / récupération de session (cf `purge-stockage.ts`).
 *
 * Caches de lecture (`parcellesCache` v2, `lotsDegorgeablesCache` v4) :
 * snapshots par tenant pour rendre les écrans terrain démarrables au
 * COLD-START hors-ligne. CLÉ PRIMAIRE = `domaineId` → une entrée par tenant,
 * jamais de mélange cross-tenant (cf `parcelles-cache.ts` pour le raisonnement
 * isolation règle #3). La lecture se fait TOUJOURS par `domaineId` courant.
 *
 * Pas d'auto-merge en cas de conflit : un échec/conflit passe la ligne en
 * statut `erreur` avec le message, remonté visiblement à l'utilisateur
 * (résolution explicite, jamais silencieuse — cf CLAUDE.md règle 5).
 */

import type { AppRouter } from "@champ-erp/api/router";
import type {
  DegorgementCreerInput,
  LotFournisseurMs,
  LotStade,
  ModeleCaisseSortie,
  MoyenReglement,
  PeseeSaisieInput,
  Remise,
  TicketExterieurCreerV2Input,
  TirageSourcesDisponiblesSortie,
} from "@champ-erp/contracts";
import type { inferRouterOutputs } from "@trpc/server";
import Dexie, { type EntityTable } from "dexie";

import type { ParcelleListItem } from "../pages/vignoble/ParcellesList.js";

import type { MaterielsPassageCache } from "./materiels-passage-cache.js";
import type { PassageVigneLocal } from "./passages-vigne-types.js";
import type { EnvoiStockAgricoleLocal } from "./stocks-agricoles.js";

/**
 * Produit vendable TEL QUE SÉRIALISÉ par tRPC (dates en `string` sur le fil, pas
 * `Date`). On stocke dans le cache exactement ce que le client reçoit — même
 * type que `venteProduits.lister` côté ERP (pattern `inferRouterOutputs` déjà
 * utilisé ailleurs, ex. `pages/vignoble/materiel`).
 */
export type ProduitVendableClient =
  inferRouterOutputs<AppRouter>["venteProduits"]["lister"][number];

/**
 * Cycle de vie d'une entrée de file de synchronisation (commun à toutes les
 * files hors-ligne : pesées, dégorgements).
 * - `en_attente` : écrite en local, pas encore poussée (réseau absent ou en file).
 * - `en_cours`   : POST tRPC en cours (verrou anti double-envoi concurrent).
 * - `envoye`     : acceptée par l'API → un `serverId` est renseigné.
 * - `erreur`     : échec/conflit → `messageErreur` rempli, retry manuel requis.
 */
export type StatutSyncEntree = "en_attente" | "en_cours" | "envoye" | "erreur";

/** Alias historique (Lot C) — conservé pour les imports existants (pesées). */
export type StatutSyncPesee = StatutSyncEntree;

/**
 * Forme COMMUNE d'une entrée de file locale (pesées, dégorgements). `id` est
 * un identifiant CLIENT (UUID) distinct de l'`id` serveur (renseigné dans
 * `serverId` après envoi réussi) — permet l'affichage immédiat de l'opération
 * avant tout aller-retour réseau. Le moteur de synchro (`sync-engine.ts`)
 * travaille sur cette forme, quel que soit le payload métier.
 */
export interface EntreeFileSync<TPayload> {
  /** Identifiant client (UUID), clé primaire locale. */
  id: string;
  /**
   * Tenant propriétaire de l'opération (isolation règle #3, alignée sur les
   * caches). Indexé : lectures/écritures TOUJOURS scopées au domaine courant —
   * la file du tenant A n'est jamais lue/poussée par une session B.
   */
  domaineId: string;
  /** Payload exact attendu par la mutation tRPC cible (contrat partagé). */
  payload: TPayload;
  /** État de synchronisation (indexé pour requêter la file efficacement). */
  statut: StatutSyncEntree;
  /** Horodatage de création locale (ms epoch) — ordre d'envoi FIFO. */
  creeLe: number;
  /** Horodatage de la dernière tentative d'envoi (ms epoch), si tentée. */
  derniereTentativeLe?: number;
  /** Nombre de tentatives d'envoi (diagnostic / backoff éventuel V1). */
  nbTentatives: number;
  /** Message d'erreur lisible (français métier) en cas d'échec/conflit. */
  messageErreur?: string;
  /** Id serveur de l'opération une fois acceptée par l'API (statut `envoye`). */
  serverId?: string;
}

/** Une pesée en file locale (payload = contrat `vendange.creerPesee`). */
export type PeseeFileLocale = EntreeFileSync<PeseeSaisieInput>;

/** Un ticket extérieur appartient à une identité locale complète, pas au seul tenant. */
export type TicketExterieurFileLocale = EntreeFileSync<TicketExterieurCreerV2Input> & {
  utilisateurId: string;
  membreId: string;
};

/** Référentiel minimal nécessaire pour continuer une pesée de caisses hors-ligne. */
export interface VendangeSaisieCacheEntry {
  domaineId: string;
  modeles?: ModeleCaisseSortie[];
  campagneOuverte?: { id: string; millesime: number };
  majLe: number;
}

/** Un dégorgement en file locale (payload = contrat `degorgements.creer`). */
export type DegorgementFileLocale = EntreeFileSync<DegorgementCreerInput>;

// ────────────────────────────────────────────────────────────────────────────
// Suivi des fermentations (lot-i É1) — file de relevés + cache grille
// ────────────────────────────────────────────────────────────────────────────

/**
 * Payload EXACT de la mutation `fermentation.enregistrerReleve` (miroir du
 * contrat `releveFermentationCreerInput`). Mappé à la main plutôt qu'importé
 * de `@champ-erp/contracts` (précédent `VenteCaveauPayload`) : la couche
 * offline ne dépend pas du câblage du contrat. `releve_le` = horodatage MÉTIER
 * de la saisie devant la cuve (ISO datetime avec offset), PAS l'heure de sync.
 */
export interface ReleveFermentationPayload {
  uuid_commande?: string;
  vin_clair_id: string;
  releve_le: string;
  densite?: number;
  temperature_c?: number;
  notes?: string;
}

/** Un relevé de fermentation en file locale (append pur, rejouable tel quel). */
export type ReleveFermentationFileLocale = EntreeFileSync<ReleveFermentationPayload>;

/**
 * Ligne de grille TELLE QUE SÉRIALISÉE par tRPC (`fermentation.grille` — dates
 * en `string` sur le fil). On stocke exactement ce que le client reçoit,
 * pattern `inferRouterOutputs` non utilisé ici pour ne pas coupler la couche
 * offline au routeur (même choix que `VenteCaveauPayload`).
 */
export interface LigneGrilleFermentationCache {
  vin_clair_id: string;
  libelle: string | null;
  millesime: number;
  cepage_id: string | null;
  statut: string;
  fml_statut: string;
  volume_actuel_l: string;
  cuve_id: string | null;
  cuve_code: string | null;
  releves_recents: Array<{
    releve_le: string;
    densite: string | null;
    temperature_c: string | null;
  }>;
  dernier_releve: {
    releve_le: string;
    densite: string | null;
    temperature_c: string | null;
  } | null;
  fin_fa_probable: boolean;
}

/**
 * Snapshot local de la grille des fermentations d'un tenant (cold-start
 * hors-ligne de la tournée É1). Clé primaire = `domaineId` (même raisonnement
 * isolation que `parcellesCache`).
 */
export interface FermentationsCacheEntry {
  /** Clé primaire = `domaine_id` du tenant propriétaire de cette grille. */
  domaineId: string;
  grille: LigneGrilleFermentationCache[];
  /** Horodatage du dernier rafraîchissement (ms epoch) — fraîcheur / diagnostic. */
  majLe: number;
}

// ────────────────────────────────────────────────────────────────────────────
// Vente rapide caveau (Lot G) — file de sync résiliente + cache + panier
// ────────────────────────────────────────────────────────────────────────────

/** Une ligne de ticket caveau en file locale (produit + quantité, remise éventuelle). */
export interface VenteCaveauLignePayload {
  produit_id: string;
  quantite: number;
  /** Remise de ligne (rarement utilisée au caveau) — jamais null sur le fil réseau. */
  remise?: NonNullable<Remise>;
}

/**
 * Payload EXACT de la mutation `venteCaveau.vente` (miroir du schéma d'entrée du
 * routeur). Stocké tel quel dans la file : rejouable à l'identique au retour du
 * réseau. Mappé à la main (types `@champ-erp/contracts`) plutôt qu'inféré de
 * `@champ-erp/api` pour ne pas coupler la couche offline au routeur serveur.
 */
export interface VenteCaveauPayload {
  /** Identité durable du geste, conservée à l'identique à chaque reprise. */
  uuid_commande?: string;
  /** Client optionnel (défaut : ticket anonyme). */
  client_id?: string;
  /** Moyen de paiement comptant (espèces / carte / chèque au caveau). */
  mode_reglement: MoyenReglement;
  /** Date de vente (défaut : aujourd'hui côté serveur). */
  date_vente?: string;
  lignes: VenteCaveauLignePayload[];
  notes?: string;
}

/**
 * Une vente caveau en file locale (payload = mutation `venteCaveau.vente`).
 * Contrairement aux pesées (append pur), chaque rejeu est VALIDÉ SERVEUR
 * (sortie de stock FIFO, verrou lot anti-TOCTOU) → un stock devenu insuffisant
 * repasse l'entrée en `erreur` avec message métier, jamais d'auto-merge.
 */
export type VenteCaveauFileLocale = EntreeFileSync<VenteCaveauPayload> & {
  /** Présent uniquement si aucun POST n'a encore été tenté, consommé avant le réseau. */
  premierEnvoiAutorise?: boolean;
  /** Refus explicite : la correction du panier crée un autre geste, jamais un retry caché. */
  rejetDefinitif?: boolean;
  /** État courant retourné au rejeu, distinct du résultat initial de la vente. */
  statutFactureActuel?: "emis" | "annule";
};

/**
 * Snapshot local du CATALOGUE vendable d'un tenant (cache hors-ligne du caveau).
 * Clé primaire = `domaineId` (même raisonnement isolation que `parcellesCache` :
 * le scoping tenant est porté par la clé elle-même). Rend l'écran de vente
 * démarrable au cold-start hors-ligne (prix TTC ronds disponibles).
 */
export interface VenteProduitsCacheEntry {
  /** Clé primaire = `domaine_id` du tenant propriétaire de ce catalogue. */
  domaineId: string;
  /** Produits actifs (tels que renvoyés par `venteProduits.lister`). */
  produits: ProduitVendableClient[];
  /** Horodatage du dernier rafraîchissement (ms epoch) — fraîcheur / diagnostic. */
  majLe: number;
}

/** Une ligne de panier caveau persistée (produit + quantité en cols). */
export interface PanierCaveauLigne {
  produit_id: string;
  quantite: number;
}

/**
 * Panier caveau EN COURS, persisté par tenant : un blip 4G ou un refresh
 * accidentel ne perd jamais la vente en cours (UX §8). Clé primaire = `domaineId`.
 */
export interface PanierCaveauEntry {
  /** Clé primaire = `domaine_id` du tenant propriétaire du panier. */
  domaineId: string;
  lignes: PanierCaveauLigne[];
  majLe: number;
}

/**
 * Projection LOCALE d'un lot dégorgeable (stade `sur_lattes`/`sur_pointes`)
 * pour le cache hors-ligne de l'écran Dégorgement (É6). Sous-ensemble de
 * `LotBouteillesSortie` (contrats) : uniquement ce qu'il faut pour scanner,
 * afficher et CONTRÔLER l'échéance 12 mois sans réseau.
 */
export interface LotDegorgeableCacheItem {
  /** Id serveur du lot (`lots_bouteilles.id`) — utilisé tel quel au POST. */
  id: string;
  /** N° de lot d'origine affichable (tirage maison ou n° lot coop) — cible du scan QR. */
  numero_lot_reference: string | null;
  reference_interne: string | null;
  origine: "tirage" | "reprise_cooperative";
  tirage_id: string | null;
  reprise_id: string | null;
  emplacement_id: string | null;
  emplacement_nom: string | null;
  /** Libellé lisible du lot (cuvée, complément) si renseigné. */
  libelle: string | null;
  /** Stade au moment du snapshot (`sur_lattes` ou `sur_pointes`). */
  stade: LotStade;
  /** Solde de bouteilles au moment du snapshot (pré-remplissage quantité). */
  nb_bouteilles: number;
  /** Format en cl (NUMERIC → string, comme l'API). */
  format_cl: string;
  /** Millésime revendiqué (null = BSA). */
  millesime: number | null;
  /** Vrai si millésimé (délai de mise en marché 36 mois). */
  est_millesime: boolean;
  /** Première date de dégorgement autorisée (tirage + 12 mois) — ISO `AAAA-MM-JJ`. */
  date_degorgement_mini: string | null;
  /** Première date de mise en marché (tirage + 15/36 mois) — ISO `AAAA-MM-JJ`. */
  date_mise_en_marche_mini: string | null;
  /**
   * Échéance 12 mois atteinte AU MOMENT DU SNAPSHOT. Indicatif : l'UI
   * recontrôle toujours par comparaison de `date_degorgement_mini` à la date
   * du jour (un cache vieux de 3 semaines peut être devenu dégorgeable).
   */
  echeance_atteinte: boolean;
}

export interface ContenantBvdCacheItem {
  id: string;
  libelle: string;
  version: string;
  volume_l: string;
  place_libre_l: string | null;
}

export interface ContexteLiquideDegorgementCache {
  version_bascule: string;
  contenants_bvd: ContenantBvdCacheItem[];
  /** Tous les contenants réels pour un constat hors stock ; absent des anciens caches. */
  contenants_reliquat?: { id: string; libelle: string }[];
  /** Réceptions physiques versionnées, distinctes des anciens constats documentaires. */
  contenants_appoint?: {
    id: string;
    libelle: string;
    version: string;
    volume_l: string;
    place_libre_l: string | null;
  }[];
  /** Inclut les donneuses déjà dégorgées : le sucre reste à mesurer ou documenter. */
  donneuses_appoint?: LotDegorgeableCacheItem[];
}

/** Zone physique du plan de cave proposée comme destination d'un lot. */
export interface EmplacementCaveDegorgementCacheItem {
  id: string;
  nom: string;
}

/**
 * Snapshot local des lots dégorgeables d'un tenant. Clé primaire = `domaineId`
 * (même raisonnement isolation que `parcellesCache` : le scoping tenant est
 * porté par la clé elle-même — cf `lots-degorgeables-cache.ts`).
 */
export interface LotsDegorgeablesCacheEntry {
  /** Clé primaire = `domaine_id` du tenant propriétaire de ces lots. */
  domaineId: string;
  /** Lots aux stades `sur_lattes`/`sur_pointes` (projection locale). */
  lots: LotDegorgeableCacheItem[];
  /** Contexte physique nécessaire au rejeu hors-ligne après bascule. */
  liquide?: ContexteLiquideDegorgementCache;
  /** Tous les emplacements du domaine, y compris les zones actuellement vides. */
  emplacements?: EmplacementCaveDegorgementCacheItem[];
  /** Lots fournisseurs de bouchons/muselets proposés au geste, tenant-scopés. */
  matieres?: LotFournisseurMs[];
  /** Sources déjà consultées pour chaque lot, versions vérifiées au rejeu. */
  sourcesAppoint?: Record<string, TirageSourcesDisponiblesSortie>;
  /** Horodatage du dernier rafraîchissement (ms epoch) — fraîcheur / diagnostic. */
  majLe: number;
}

/**
 * Snapshot local des parcelles d'un tenant (cache hors-ligne du sélecteur de
 * pesée). Clé primaire = `domaineId` : le scoping tenant est porté par la clé
 * elle-même, pas par un simple champ filtrable → aucun risque de servir la
 * liste du tenant A à une session du tenant B (cf `parcelles-cache.ts`).
 */
export interface ParcellesCacheEntry {
  /** Clé primaire = `domaine_id` du tenant propriétaire de ces parcelles. */
  domaineId: string;
  /** Liste NON filtrée des parcelles (telle que renvoyée par `parcellaire.lister({})`). */
  parcelles: ParcelleListItem[];
  /** Horodatage du dernier rafraîchissement (ms epoch) — fraîcheur / diagnostic. */
  majLe: number;
}

/**
 * Base Dexie typée. Schéma versionné : incrémenter `version(n)` à chaque
 * évolution de schéma (Dexie gère la migration des stores).
 */
export class OfflineDb extends Dexie {
  /** File d'attente des pesées à synchroniser. */
  peseesQueue!: EntityTable<PeseeFileLocale, "id">;
  /** File d'attente des tickets de pesée extérieurs. */
  ticketsExterieursQueue!: EntityTable<TicketExterieurFileLocale, "id">;
  /** Cache des parcelles par tenant (clé = `domaineId`). */
  parcellesCache!: EntityTable<ParcellesCacheEntry, "domaineId">;
  /** File d'attente des dégorgements à synchroniser (Lot D, É6). */
  degorgementsQueue!: EntityTable<DegorgementFileLocale, "id">;
  /** Cache des lots dégorgeables par tenant (clé = `domaineId`). */
  lotsDegorgeablesCache!: EntityTable<LotsDegorgeablesCacheEntry, "domaineId">;
  /** File d'attente des ventes caveau à synchroniser (Lot G). */
  venteCaveauQueue!: EntityTable<VenteCaveauFileLocale, "id">;
  /** Cache du catalogue vendable par tenant (clé = `domaineId`). */
  venteProduitsCache!: EntityTable<VenteProduitsCacheEntry, "domaineId">;
  /** Panier caveau en cours, persisté par tenant (clé = `domaineId`). */
  panierCaveauCache!: EntityTable<PanierCaveauEntry, "domaineId">;
  /** File d'attente des relevés de fermentation (lot-i É1). */
  relevesFermentationQueue!: EntityTable<ReleveFermentationFileLocale, "id">;
  /** Cache de la grille des fermentations par tenant (clé = `domaineId`). */
  fermentationsCache!: EntityTable<FermentationsCacheEntry, "domaineId">;
  /** Brouillons et commandes scellées ; jamais repris par le moteur legacy. */
  passagesVigne!: EntityTable<PassageVigneLocal, "id">;
  /** Références minimales consultées par une identité, pas une file d'opérations. */
  materielsPassageCache!: EntityTable<MaterielsPassageCache, "cle">;
  stocksAgricoles!: EntityTable<EnvoiStockAgricoleLocal, "id">;
  vendangeSaisieCache!: EntityTable<VendangeSaisieCacheEntry, "domaineId">;

  constructor() {
    super("champ-erp-offline");
    // v1 : on indexe `id` (PK), `statut` (filtrage file) et `creeLe` (ordre FIFO).
    this.version(1).stores({
      peseesQueue: "id, statut, creeLe",
    });
    // v2 : ajout du cache parcelles keyé par `domaineId` (clé primaire = scope
    // tenant). `peseesQueue` inchangé — redéclaré tel quel (exigence Dexie).
    this.version(2).stores({
      peseesQueue: "id, statut, creeLe",
      parcellesCache: "domaineId",
    });
    // v3 (LOT 1 sécu 2026-07-04) : la file des pesées devient tenant-scopée —
    // `domaineId` indexé sur chaque enregistrement (aligné sur `parcellesCache`).
    // Migration : on PURGE les entrées legacy (sans `domaineId`) — données de
    // dev uniquement, inrattachables à un tenant de façon fiable.
    this.version(3)
      .stores({
        peseesQueue: "id, statut, creeLe, domaineId",
        parcellesCache: "domaineId",
      })
      .upgrade(async (tx) => {
        await tx.table("peseesQueue").clear();
      });
    // v4 (Lot D cave, 2026-07-05) : file des dégorgements (calquée sur
    // `peseesQueue`, tenant-scopée d'entrée de jeu) + cache des lots
    // dégorgeables (calqué sur `parcellesCache`, clé primaire = `domaineId`).
    // Stores existants redéclarés tels quels (exigence Dexie).
    this.version(4).stores({
      peseesQueue: "id, statut, creeLe, domaineId",
      parcellesCache: "domaineId",
      degorgementsQueue: "id, statut, creeLe, domaineId",
      lotsDegorgeablesCache: "domaineId",
    });
    // v5 (Lot G ventes, 2026-07-08) : vente rapide caveau. File de sync
    // résiliente `venteCaveauQueue` (tenant-scopée, calquée sur pesées/
    // dégorgements), cache catalogue `venteProduitsCache` (clé = `domaineId`,
    // cold-start hors-ligne) et panier persisté `panierCaveauCache` (un blip 4G
    // ou un refresh ne perd pas la vente). Stores existants redéclarés tels
    // quels (exigence Dexie).
    this.version(5).stores({
      peseesQueue: "id, statut, creeLe, domaineId",
      parcellesCache: "domaineId",
      degorgementsQueue: "id, statut, creeLe, domaineId",
      lotsDegorgeablesCache: "domaineId",
      venteCaveauQueue: "id, statut, creeLe, domaineId",
      venteProduitsCache: "domaineId",
      panierCaveauCache: "domaineId",
    });
    // v6 (lot-i É1, 2026-07-23) : suivi des fermentations. File des
    // relevés densité/température (calquée sur `peseesQueue`, tenant-scopée
    // d'entrée de jeu) + cache de la grille multi-cuves (calqué sur
    // `parcellesCache`, clé primaire = `domaineId`). Stores existants
    // redéclarés tels quels (exigence Dexie).
    this.version(6).stores({
      peseesQueue: "id, statut, creeLe, domaineId",
      parcellesCache: "domaineId",
      degorgementsQueue: "id, statut, creeLe, domaineId",
      lotsDegorgeablesCache: "domaineId",
      venteCaveauQueue: "id, statut, creeLe, domaineId",
      venteProduitsCache: "domaineId",
      panierCaveauCache: "domaineId",
      relevesFermentationQueue: "id, statut, creeLe, domaineId",
      fermentationsCache: "domaineId",
    });
    // v7 : propriétaire = utilisateur × membre × domaine, pas seulement tenant.
    // statut/domaineId intègrent les brouillons aux gardes de sortie/quarantaine.
    this.version(7).stores({
      peseesQueue: "id, statut, creeLe, domaineId",
      parcellesCache: "domaineId",
      degorgementsQueue: "id, statut, creeLe, domaineId",
      lotsDegorgeablesCache: "domaineId",
      venteCaveauQueue: "id, statut, creeLe, domaineId",
      venteProduitsCache: "domaineId",
      panierCaveauCache: "domaineId",
      relevesFermentationQueue: "id, statut, creeLe, domaineId",
      fermentationsCache: "domaineId",
      passagesVigne: "id, [utilisateurId+membreId+domaineId], domaineId, statut, creeLe",
    });
    // Les autres stores restent inchangés ; aucune réécriture des commandes v1.
    this.version(8).stores({
      materielsPassageCache: "cle, [utilisateurId+membreId+domaineId], domaineId",
    });
    // Envoi manuel seulement ; l'index statut intègre purge, garde de sortie et quarantaine.
    this.version(9).stores({
      stocksAgricoles: "id, [utilisateurId+membreId+domaineId], domaineId, statut, creeLe",
    });
    // Les modèles sont immuables : leur snapshot permet la saisie au pressoir sans réseau.
    this.version(10).stores({
      vendangeSaisieCache: "domaineId",
    });
    // v11 : tickets extérieurs, enregistrés localement avant synchronisation.
    this.version(11).stores({
      ticketsExterieursQueue: "id, statut, creeLe, domaineId",
    });
    // v12 : une session du même domaine ne lit ni ne pousse les tickets locaux
    // d'un autre utilisateur/membre.
    this.version(12).stores({
      ticketsExterieursQueue: "id, [utilisateurId+membreId+domaineId], domaineId, statut, creeLe",
    });
  }
}

/** Instance singleton partagée par toute l'app (1 connexion IndexedDB). */
export const offlineDb = new OfflineDb();
